import { useEffect, useRef, useState } from 'react';

interface Props {
  /** PNG как data URL после каждого штриха; null — пусто */
  onChange: (dataUrl: string | null) => void;
  height?: number;
  clearLabel: string;
  hint: string;
}

/** Подпись пальцем или стилусом на планшете (этап 1.2б): canvas с pointer events, результат — PNG data URL. */
export default function SignaturePad({ onChange, height = 160, clearLabel, hint }: Props) {
  const ref = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);
  const [empty, setEmpty] = useState(true);

  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const ratio = window.devicePixelRatio || 1;
    const w = c.clientWidth;
    c.width = w * ratio;
    c.height = height * ratio;
    const ctx = c.getContext('2d');
    if (ctx) {
      ctx.scale(ratio, ratio);
      ctx.lineWidth = 2.2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = '#1e1e20';
    }
  }, [height]);

  const pos = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };
  const start = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const ctx = ref.current?.getContext('2d');
    if (!ctx) return;
    drawing.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  };
  const move = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    const ctx = ref.current?.getContext('2d');
    if (!ctx) return;
    const p = pos(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    if (empty) setEmpty(false);
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    onChange(ref.current?.toDataURL('image/png') ?? null);
  };
  const clear = () => {
    const c = ref.current;
    const ctx = c?.getContext('2d');
    if (!c || !ctx) return;
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.restore();
    setEmpty(true);
    onChange(null);
  };

  return (
    <div>
      <canvas
        ref={ref}
        style={{ height, touchAction: 'none' }}
        className="w-full cursor-crosshair rounded-md border border-dashed border-background-300 bg-background-50"
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerCancel={end}
        onPointerLeave={end}
      />
      <div className="mt-1.5 flex items-center justify-between text-[11px] text-foreground-500">
        <span>{hint}</span>
        <button type="button" onClick={clear} disabled={empty} className="cursor-pointer font-medium text-primary-700 disabled:cursor-default disabled:opacity-40">
          {clearLabel}
        </button>
      </div>
    </div>
  );
}
